// OEM ORDER EVENTS, STATUS AND THE ORDER STATUS VIEW -- Order Management O6 +
// O7 (owner decisions D181 / D184, 2026-10-30).
//
// ⚠️ THE STATUS IS THE SERVER'S. It is derived from the recorded events every
// time it is read and never stored; this file only names it. Late and at-risk
// are the server's too, each with its reasons.
//
// ⚠️ QUANTITIES ARE SENT AS TYPED. An empty quantity or date travels as null
// and the server refuses it with a reason -- absent is never zero. Shipping
// above ordered, or delivering above shipped, is refused by the server, and
// the refusal is shown as it came.
//
// ⚠️ A MOVED PROMISE IS SHOWN BESIDE THE ORIGINAL, never in place of it.
//
// ⚠️ NOTHING HERE PRICES. Quantities only; the quote's figures never move.

import { useCallback, useEffect, useState } from "react";
import {
  getOrderStatus, recordOemOrderEvent, attachPod,
  type OemOrder, type OemOrderEventKind, type OemOrderStatusName, type OrderStatusView,
} from "@/services/api";
import { formatDate, formatTimestampDate } from "@/lib/format";
import { Pill } from "@/components/OemConfirmationsPanel";
import { CustomerFlagBadges, NOT_STATED } from "@/components/CustomerDates";
import { orderNextText } from "@/lib/dealProgress";
import { colors, semanticColors, radius, spacing, heights, typography } from "@/styles/tokens";

const TD: React.CSSProperties = { padding: `${spacing[6]}px ${spacing[8]}px`, fontSize: typography.fontSize.sm, borderBottom: `1px solid ${semanticColors.border}`, verticalAlign: "top" };
const TH: React.CSSProperties = { ...TD, textAlign: "left", color: semanticColors.textMuted, fontWeight: typography.fontWeight.medium, whiteSpace: "nowrap" };
const NUM: React.CSSProperties = { ...TD, textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" };
const BTN: React.CSSProperties = {
  height: heights.input, padding: `0 ${spacing[10]}px`, backgroundColor: semanticColors.surface,
  color: semanticColors.textSecondary, border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
  fontSize: typography.fontSize.sm, cursor: "pointer", whiteSpace: "nowrap",
};
const PRIMARY: React.CSSProperties = { ...BTN, backgroundColor: colors.indigo600, color: semanticColors.onAccent, border: "none" };
const INPUT: React.CSSProperties = {
  height: heights.input, padding: `0 ${spacing[8]}px`, border: `1px solid ${semanticColors.border}`,
  borderRadius: radius.base, fontSize: typography.fontSize.sm, backgroundColor: semanticColors.surface,
  color: semanticColors.textPrimary, minWidth: 0,
};
const NOTE: React.CSSProperties = { fontSize: typography.fontSize.sm, color: semanticColors.textSecondary };

export const STATUS_LABEL: Record<OemOrderStatusName, string> = {
  placed: "Placed", acknowledged: "Acknowledged", partially_shipped: "Partially shipped",
  shipped: "Shipped", partially_delivered: "Partially delivered", delivered: "Delivered", cancelled: "Cancelled",
};
const STATUS_TONE: Record<OemOrderStatusName, "green" | "amber" | "red" | "gray"> = {
  placed: "gray", acknowledged: "gray", partially_shipped: "amber", shipped: "amber", partially_delivered: "amber",
  delivered: "green", cancelled: "gray",
};
const KIND_LABEL: Record<OemOrderEventKind, string> = {
  acknowledged: "Acknowledged", shipped: "Shipped", delivered: "Delivered", cancelled: "Cancelled",
};

export function StatusPill({ status, testId }: { status: OemOrderStatusName; testId?: string }) {
  return <Pill tone={STATUS_TONE[status]} testId={testId}>{STATUS_LABEL[status] ?? status}</Pill>;
}

/** Late / At risk, each with the server's reasons as a tooltip. */
export function FlagBadges({ late, atRisk, reasons, testId }: {
  late?: boolean; atRisk?: boolean; reasons?: string[]; testId?: string;
}) {
  if (!late && !atRisk) return null;
  const title = (reasons ?? []).join("\n") || undefined;
  return (
    <span data-testid={testId} title={title} style={{ display: "inline-flex", gap: spacing[4] }}>
      {late && <Pill tone="red">Late</Pill>}
      {atRisk && <Pill tone="amber">At risk</Pill>}
    </span>
  );
}

const fmtQty = (n: number | null | undefined) => (n == null ? "—" : String(n));

/** Summed over the order's lines; an unreported quantity is not counted as 0. */
function sums(order: OemOrder) {
  let ordered = 0, shipped = 0, delivered = 0;
  for (const l of order.lines) {
    ordered += l.qty_ordered;
    if (l.shipped_qty != null) shipped += l.shipped_qty;
    if (l.delivered_qty != null) delivered += l.delivered_qty;
  }
  return { ordered, shipped, delivered };
}

/** THE ORDER'S MINI PROGRESS LINE (ux-guided-deal): Placed -> Acknowledged ->
 *  Shipped -> Delivered, or Cancelled. Read from the SERVER's derived status
 *  and recorded events only; partial quantities are said, never rounded up. */
export function OrderProgressLine({ order, testId }: { order: OemOrder; testId?: string }) {
  const { ordered, shipped, delivered } = sums(order);
  const acked = (order.events ?? []).some((e) => e.kind === "acknowledged") || order.status === "acknowledged";
  type S = { label: string; state: "done" | "part" | "todo" | "skipped" };
  const steps: S[] = [
    { label: "Placed", state: "done" },
    { label: acked || shipped <= 0 ? "Acknowledged" : "Acknowledged (not recorded)",
      state: acked ? "done" : shipped > 0 ? "skipped" : "todo" },
    { label: shipped > 0 && shipped < ordered ? `Shipped ${shipped} of ${ordered}` : "Shipped",
      state: ordered > 0 && shipped >= ordered ? "done" : shipped > 0 ? "part" : "todo" },
    { label: (delivered > 0 && delivered < ordered ? `Delivered ${delivered} of ${ordered}` : "Delivered")
        + (order.ship_to_kind === "warehouse" ? " (received)" : ""),
      state: ordered > 0 && delivered >= ordered ? "done" : delivered > 0 ? "part" : "todo" },
  ];
  const tone = (st: S["state"]) => (st === "done" ? colors.green600 : st === "part" ? colors.amber800 : semanticColors.textMuted);
  return (
    <ol data-testid={testId} aria-label={`Order progress: ${STATUS_LABEL[order.status] ?? order.status}`}
      style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexWrap: "wrap", alignItems: "center",
        gap: spacing[4], fontSize: typography.fontSize.xs }}>
      {order.status === "cancelled" ? (
        <li data-state="cancelled" style={{ color: colors.red600, fontWeight: typography.fontWeight.medium }}>Placed → Cancelled</li>
      ) : steps.map((st, i) => (
        <li key={st.label} data-state={st.state} style={{ display: "inline-flex", alignItems: "center", gap: spacing[4],
          color: tone(st.state), fontWeight: st.state === "todo" ? typography.fontWeight.regular : typography.fontWeight.medium,
          textDecoration: st.state === "skipped" ? "line-through" : undefined }}>
          {i > 0 && <span aria-hidden style={{ color: semanticColors.textPlaceholder }}>→</span>}
          <span>{st.state === "done" ? "✓ " : ""}{st.label}</span>
        </li>
      ))}
    </ol>
  );
}

/** The order's events, oldest first, and (for those who may record) the form. */
export function OrderEvents({ order, canRecord, onRecorded }: {
  order: OemOrder; canRecord: boolean; onRecorded: () => void;
}) {
  const events = order.events ?? [];
  const closed = order.status === "cancelled" || order.status === "delivered";
  return (
    <div data-testid={`oemo-events-${order.id}`} style={{ padding: spacing[12], backgroundColor: semanticColors.panelBg }}>
      <div style={{ marginBottom: spacing[8], display: "grid", gap: spacing[4] }}>
        <OrderProgressLine order={order} testId={`oemo-progress-${order.id}`} />
        <div data-testid={`oemo-next-${order.id}`} style={{ ...NOTE, color: semanticColors.textPrimary }}>{orderNextText(order)}</div>
      </div>
      <div style={{ ...NOTE, marginBottom: spacing[8] }}>
        Status is worked out from what has been recorded here. Nothing is edited or deleted; a mistake is put right by a later entry.
      </div>
      {(order.flag_reasons ?? []).length > 0 && (
        <ul data-testid={`oemo-flag-reasons-${order.id}`} style={{ margin: `0 0 ${spacing[8]}px`, paddingLeft: spacing[16], ...NOTE, color: colors.amber800 }}>
          {order.flag_reasons!.map((r) => <li key={r}>{r}</li>)}
        </ul>
      )}
      <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: spacing[8] }}>
        <thead><tr>{["Part", "Ordered", "Shipped", "Delivered", "Promised (placed)", "Promised now"].map((h) => <th key={h} style={TH}>{h}</th>)}</tr></thead>
        <tbody>
          {order.lines.map((l) => {
            const moved = l.current_promised_date && l.current_promised_date !== l.promised_date;
            return (
              <tr key={l.id} data-testid={`oemo-line-${l.id}`}>
                <td style={TD}>{l.part_number}</td>
                <td style={NUM}>{l.qty_ordered}</td>
                <td style={NUM}>{fmtQty(l.shipped_qty)}</td>
                <td style={NUM}>{fmtQty(l.delivered_qty)}</td>
                <td style={TD}>{l.promised_date ? formatDate(l.promised_date) : "Not stated"}</td>
                <td style={{ ...TD, color: moved ? colors.amber800 : undefined }} data-testid={`oemo-promise-now-${l.id}`}>
                  {l.current_promised_date ? formatDate(l.current_promised_date) : "Not stated"}{moved ? " (moved by the OEM)" : ""}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {events.length === 0 ? <div style={NOTE}>Nothing recorded since the order was placed.</div> : (
        <ol data-testid={`oemo-history-${order.id}`} style={{ margin: 0, paddingLeft: spacing[16], ...NOTE }}>
          {events.map((e) => (
            <li key={e.id} data-testid={`oemo-event-${e.id}`} style={{ marginBottom: spacing[4] }}>
              <strong style={{ color: semanticColors.textPrimary }}>{KIND_LABEL[e.kind]}</strong> {formatDate(e.event_date)}
              {e.oem_reference ? ` · ref ${e.oem_reference}` : ""}
              {e.lines.length > 0 ? ` · ${e.lines.map((x) => x.qty != null ? `${x.part_number} × ${x.qty}`
                : `${x.part_number} promised ${x.promised_before ? formatDate(x.promised_before) : "not stated"} → ${x.promised_after ? formatDate(x.promised_after) : "—"}`).join(", ")}` : ""}
              {e.reason ? ` · reason: ${e.reason}` : ""}
              {e.kind === "delivered" && order.ship_to_kind === "customer" && (
                <PodControl eventId={e.id} count={e.pod_count ?? 0} canRecord={canRecord} onDone={onRecorded} />
              )}
              {e.note ? ` · ${e.note}` : ""}
              {` · recorded by ${e.recorded_by ?? "—"}${e.recorded_at ? ` on ${formatTimestampDate(e.recorded_at)}` : ""}`}
            </li>
          ))}
        </ol>
      )}
      {canRecord && !closed && <RecordEvent key={`${order.status}-${events.length}`} order={order} onRecorded={onRecorded} />}
    </div>
  );
}

/** The events that make sense next, best first. The SERVER still decides:
 *  cancelling after a shipment, shipping above ordered or delivering above
 *  shipped is refused there, with its reason. */
export function sensibleEvents(order: OemOrder): OemOrderEventKind[] {
  const { ordered, shipped, delivered } = sums(order);
  const acked = (order.events ?? []).some((e) => e.kind === "acknowledged");
  const out: OemOrderEventKind[] = [];
  if (!acked && shipped <= 1e-9) out.push("acknowledged");
  if (shipped < ordered - 1e-9) out.push("shipped");
  // M4 (owner Q2): a via-warehouse order is delivered by its goods receipts
  // (GRN), never by a Delivered event -- the server refuses one.
  if (order.ship_to_kind !== "warehouse" && delivered < shipped - 1e-9) out.push("delivered");
  out.push("acknowledged");                                       // later: a moved promised date
  out.push("cancelled");
  return out.filter((k, i) => out.indexOf(k) === i);
}

const KIND_OPTION: Record<OemOrderEventKind, string> = {
  acknowledged: "OEM acknowledged", shipped: "Shipped", delivered: "Delivered", cancelled: "Cancelled",
};

function RecordEvent({ order, onRecorded }: { order: OemOrder; onRecorded: () => void }) {
  const offered = sensibleEvents(order);
  const acked = (order.events ?? []).some((e) => e.kind === "acknowledged");
  const [kind, setKind] = useState<OemOrderEventKind>(offered[0]);
  const [date, setDate] = useState("");
  const [ref, setRef] = useState("");
  const [reason, setReason] = useState("");
  const [vals, setVals] = useState<Record<number, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const perLine = kind !== "cancelled";

  const submit = async () => {
    setBusy(true); setError("");
    const lines = kind === "acknowledged"
      ? order.lines.filter((l) => (vals[l.id] ?? "") !== "").map((l) => ({ order_line_id: l.id, promised_date: vals[l.id] }))
      : kind === "cancelled" ? undefined
        // Only lines with something typed; an empty one stays ABSENT, never 0.
        : order.lines.filter((l) => (vals[l.id] ?? "").trim() !== "").map((l) => ({ order_line_id: l.id, qty: Number(vals[l.id]) }));
    const res = await recordOemOrderEvent(order.id, {
      kind, event_date: date || null, oem_reference: ref || undefined,
      reason: kind === "cancelled" ? reason : undefined, lines,
    });
    setBusy(false);
    if (!res.ok) { setError(res.error || "Not recorded."); return; }
    setVals({}); setRef(""); setReason(""); setDate("");
    onRecorded();
  };

  return (
    <div data-testid={`oemo-record-${order.id}`} style={{ marginTop: spacing[12], display: "grid", gap: spacing[8] }}>
      <div style={{ display: "flex", gap: spacing[8], flexWrap: "wrap", alignItems: "center" }}>
        <select data-testid={`oemo-ev-kind-${order.id}`} style={INPUT} value={kind}
          onChange={(e) => { setKind(e.target.value as OemOrderEventKind); setVals({}); }}>
          {offered.map((k) => (
            <option key={k} value={k}>
              {k === "acknowledged" && acked ? "OEM changed the promised date"
                : k === "cancelled" ? "Cancelled (only before anything ships)" : KIND_OPTION[k]}
            </option>
          ))}
        </select>
        <label style={{ ...NOTE, display: "inline-flex", alignItems: "center", gap: spacing[4] }}>
          Date it happened
          <input type="date" aria-label="Date it happened" data-testid={`oemo-ev-date-${order.id}`} style={INPUT}
            value={date} onChange={(e) => setDate(e.target.value)} />
        </label>
        <input aria-label="OEM reference" placeholder="OEM reference (ack no., AWB, invoice)" data-testid={`oemo-ev-ref-${order.id}`}
          style={{ ...INPUT, flex: "1 1 200px" }} value={ref} onChange={(e) => setRef(e.target.value)} />
      </div>
      {kind === "cancelled" && (
        <input aria-label="Why it was cancelled" placeholder="Why it was cancelled (required)" data-testid={`oemo-ev-reason-${order.id}`}
          style={INPUT} value={reason} onChange={(e) => setReason(e.target.value)} />
      )}
      {perLine && order.lines.map((l) => (
        <label key={l.id} style={{ ...NOTE, display: "flex", gap: spacing[8], alignItems: "center" }}>
          <span style={{ minWidth: 180 }}>{l.part_number}</span>
          {kind === "acknowledged" ? (
            <input type="date" aria-label={`New promised date for ${l.part_number}`} data-testid={`oemo-ev-promise-${l.id}`}
              style={INPUT} value={vals[l.id] ?? ""} onChange={(e) => setVals((v) => ({ ...v, [l.id]: e.target.value }))} />
          ) : (
            <input inputMode="decimal" aria-label={`Quantity ${kind} for ${l.part_number}`} data-testid={`oemo-ev-qty-${l.id}`}
              style={{ ...INPUT, width: 90 }} value={vals[l.id] ?? ""} onChange={(e) => setVals((v) => ({ ...v, [l.id]: e.target.value }))} />
          )}
          <span>
            {kind === "acknowledged" ? "only if the OEM moved the date" : kind === "shipped"
              ? `${fmtQty(l.shipped_qty)} of ${l.qty_ordered} shipped so far` : `${fmtQty(l.delivered_qty)} of ${fmtQty(l.shipped_qty)} shipped delivered so far`}
          </span>
          {kind !== "acknowledged" && (() => {
            // NEVER PREFILLED: the user clicks to take what is left. Absent stays absent.
            const left = kind === "shipped" ? l.qty_ordered - (l.shipped_qty ?? 0) : (l.shipped_qty ?? 0) - (l.delivered_qty ?? 0);
            if (left <= 1e-9) return null;
            const n = String(Math.round(left * 1000) / 1000);
            return (
              <button type="button" data-testid={`oemo-ev-remaining-${l.id}`} style={BTN}
                onClick={() => setVals((v) => ({ ...v, [l.id]: n }))}>Use remaining quantity ({n})</button>
            );
          })()}
        </label>
      ))}
      {error && <div role="alert" data-testid={`oemo-ev-error-${order.id}`} style={{ ...NOTE, color: colors.red600 }}>{error}</div>}
      <div>
        <button type="button" data-testid={`oemo-ev-save-${order.id}`} style={busy ? { ...PRIMARY, backgroundColor: colors.gray300, cursor: "not-allowed" } : PRIMARY}
          disabled={busy} onClick={() => { void submit(); }}>{busy ? "Recording…" : "Record"}</button>
      </div>
    </div>
  );
}

/** M4: the proof of delivery of a drop-ship Delivered event -- optional. */
function PodControl({ eventId, count, canRecord, onDone }: {
  eventId: number; count: number; canRecord: boolean; onDone: () => void;
}) {
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const upload = async (file: File) => {
    setBusy(true); setError("");
    const b64 = await new Promise<string>((resolve, reject) => {
      const r = new FileReader();
      r.onerror = () => reject(new Error("The file could not be read."));
      r.onload = () => { const t = String(r.result ?? ""); resolve(t.slice(t.indexOf(",") + 1)); };
      r.readAsDataURL(file);
    }).catch((e: Error) => { setError(e.message); return ""; });
    if (b64) {
      const res = await attachPod(eventId, file.name, b64);
      if (!res.ok) setError(res.error || "The proof of delivery was not attached."); else onDone();
    }
    setBusy(false);
  };
  return (
    <span style={{ marginLeft: spacing[6] }}>
      <span data-testid={`oemo-pod-count-${eventId}`}>
        {count ? ` · proof of delivery: ${count} file${count === 1 ? "" : "s"}` : " · no proof of delivery attached"}
      </span>
      {canRecord && (
        <label style={{ ...BTN, display: "inline-flex", alignItems: "center", marginLeft: spacing[6] }}>
          {busy ? "Attaching…" : "Attach proof of delivery"}
          <input data-testid={`oemo-pod-upload-${eventId}`} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp"
            style={{ display: "none" }}
            onChange={(ev) => { const f = ev.target.files?.[0]; ev.target.value = ""; if (f) void upload(f); }} />
        </label>
      )}
      {error && <span role="alert" style={{ color: colors.red600, marginLeft: spacing[6] }}>{error}</span>}
    </span>
  );
}

/** O7: the customer order, line by line. */
export function OrderStatusPanel({ quoteId, reloadKey = 0 }: { quoteId: number; reloadKey?: number }) {
  const [view, setView] = useState<OrderStatusView | null>(null);
  const [error, setError] = useState("");
  const [hidden, setHidden] = useState(false);
  const load = useCallback(async () => {
    const res = await getOrderStatus(quoteId);
    if (res.notFound) { setHidden(true); return; }
    if (!res.ok) { setError(res.error || "Could not read the order status."); return; }
    setHidden(false); setError(""); setView(res.view!);
  }, [quoteId]);
  useEffect(() => { void load(); }, [load, reloadKey]);
  if (hidden) return null;
  if (error) return <div role="alert" style={{ ...NOTE, color: colors.red600, padding: spacing[12] }}>{error}</div>;
  if (!view || view.orders.length === 0) return null;
  return (
    <div data-testid="order-status" style={{ borderBottom: `1px solid ${semanticColors.border}` }}>
      <div style={{ display: "flex", gap: spacing[8], alignItems: "center", padding: `${spacing[8]}px ${spacing[12]}px` }}>
        <span style={{ fontWeight: typography.fontWeight.medium, fontSize: typography.fontSize.sm }}>Order status by line</span>
        <FlagBadges late={view.late} atRisk={view.at_risk} testId="order-status-flags" />
        {!view.reconciles && <Pill tone="red" testId="order-status-breaks">Quantities do not reconcile</Pill>}
        <CustomerFlagBadges promiseAfter={view.promise_after_customer} atRisk={view.customer_date_at_risk}
          testId="order-status-cflags" />
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr>{["#", "Part", "Quote qty", "Confirmed", "Ordered", "Shipped", "Delivered", "To order", "To deliver", "OEM orders", "Customer date", ""].map((h, i) => (
            <th key={h || i} style={i >= 2 && i <= 8 ? { ...TH, textAlign: "right" } : TH}>{h}</th>))}</tr></thead>
          <tbody>
            {view.lines.map((l) => (
              <tr key={l.line_id} data-testid={`os-line-${l.line_no}`}>
                <td style={TD}>{l.line_no}</td>
                <td style={TD}>{l.part_number}{l.oem ? <span style={{ color: semanticColors.textMuted }}> · {l.oem}</span> : null}</td>
                <td style={NUM}>{l.qty}</td>
                <td style={NUM}>{l.confirmed}</td>
                <td style={NUM}>{l.ordered}</td>
                <td style={NUM}>{l.shipped}</td>
                <td style={NUM}>{l.delivered}</td>
                <td style={NUM} data-testid={`os-left-order-${l.line_no}`}>{l.not_quoted ? "Not quoted" : l.left_to_order}</td>
                <td style={NUM} data-testid={`os-left-deliver-${l.line_no}`}>{l.left_to_deliver}</td>
                <td style={TD}>{l.orders.length === 0 ? <span style={{ color: semanticColors.textMuted }}>None</span>
                  : l.orders.map((o) => (
                    <div key={o.order_id} style={{ display: "flex", gap: spacing[4], alignItems: "center", flexWrap: "wrap" }}>
                      <span>{o.number} × {o.qty_ordered}</span><StatusPill status={o.status} />
                    </div>))}
                </td>
                <td style={{ ...TD, whiteSpace: "nowrap" }} data-testid={`os-cdate-${l.line_no}`}>
                  {l.customer_promised_date ? formatDate(l.customer_promised_date)
                    : <span style={{ color: semanticColors.textMuted }}>{l.not_quoted ? "—" : NOT_STATED}</span>}
                </td>
                <td style={TD}>
                  <FlagBadges late={l.late} atRisk={l.at_risk} testId={`os-flags-${l.line_no}`} />
                  <CustomerFlagBadges promiseAfter={l.promise_after_customer} atRisk={l.customer_date_at_risk}
                    testId={`os-cflags-${l.line_no}`} />
                  {!l.reconciles && <div data-testid={`os-breaks-${l.line_no}`} style={{ color: colors.red600, fontSize: typography.fontSize.xs }}>{l.breaks.join("; ")}</div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
